import { useNavigationStore } from '~/stores/navigation';
import { ROUTE_METADATA } from '~/config/routeMetadata';
import type { BreadcrumbItem } from '~/stores/navigation';

/**
 * Build hierarchical breadcrumb chain for a route
 * Returns array from root parent down to current route
 */
function buildHierarchicalChain(routeName: string, routeParams: any, routeFullPath: string): BreadcrumbItem[] {
  const metadata = ROUTE_METADATA[routeName];
  if (!metadata) return [];

  const chain: BreadcrumbItem[] = [];

  // Recursively build parent chain first
  if (metadata.parentRoute) {
    const parentMetadata = ROUTE_METADATA[metadata.parentRoute];
    if (parentMetadata) {
      const parentUrl = `/${metadata.parentRoute.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase().replace('lms-', 'lms/')}`;

      chain.push({
        label: parentMetadata.labelGenerator({}, null),
        routeName: metadata.parentRoute,
        params: {},
        url: parentUrl,
      });
    }
  }

  // Add current route
  chain.push({
    label: metadata.labelGenerator(routeParams, null),
    routeName: routeName,
    params: { ...routeParams } as Record<string, string>,
    url: routeFullPath,
    meta: {
      entityType: metadata.entityType,
      entitySlug: routeParams.slug as string || routeParams.courseSlug as string,
    },
  });

  return chain;
}

/**
 * Get the root parent for a route (the top-level section it belongs to)
 */
function getRootParent(routeName: string): string | null {
  const metadata = ROUTE_METADATA[routeName];
  if (!metadata) return null;

  // If route has no parent, it IS the root
  if (!metadata.parentRoute) return routeName;

  // Otherwise, get the parent's root
  return getRootParent(metadata.parentRoute);
}

/**
 * Check if navigation is within the same section hierarchy
 */
function isSameSection(fromRouteName: string, toRouteName: string): boolean {
  const fromRoot = getRootParent(fromRouteName);
  const toRoot = getRootParent(toRouteName);

  return fromRoot === toRoot;
}

/**
 * Track navigation for breadcrumb trail
 * Exported for testing purposes
 */
export function trackNavigation(to: any, from: any) {
  const routeMetadata = ROUTE_METADATA[to.name as string];

  // Ignore routes without metadata or non-breadcrumbable routes
  if (!routeMetadata || !routeMetadata.breadcrumbable) {
    return;
  }

  const navigationStore = useNavigationStore();

  // Contextual routes maintain previous breadcrumb context
  if (routeMetadata.contextual && from && from.name) {
    const fromMetadata = ROUTE_METADATA[from.name as string];

    // If coming from a breadcrumbable route, keep the chain and add this route
    if (fromMetadata && fromMetadata.breadcrumbable) {
      const breadcrumbItem: BreadcrumbItem = {
        label: routeMetadata.labelGenerator(to.params, null),
        routeName: to.name as string,
        params: { ...to.params } as Record<string, string>,
        url: to.fullPath,
        meta: {
          entityType: routeMetadata.entityType,
          entitySlug: to.params.slug as string || to.params.courseSlug as string,
        },
      };

      // Sibling navigation (e.g., lesson → lesson via Next/Previous):
      // replace the last entry instead of appending to prevent breadcrumb accumulation
      if (from.name === to.name) {
        navigationStore.replaceLastInNavigationChain(breadcrumbItem);
      } else {
        navigationStore.addToNavigationChain(breadcrumbItem);
      }
      return;
    }
  }

  // For non-contextual routes: Build complete hierarchical chain
  // This ensures breadcrumbs show parent > child, not navigation history
  const hierarchicalChain = buildHierarchicalChain(to.name, to.params, to.fullPath);

  // Replace navigation chain with hierarchical chain
  navigationStore.clearNavigationChain();
  hierarchicalChain.forEach(item => {
    navigationStore.addToNavigationChain(item);
  });
}

/**
 * Global middleware to track navigation for breadcrumbs
 */
export default defineNuxtRouteMiddleware((to, from) => {
  // Skip on server-side
  if (process.server) return;

  trackNavigation(to, from);
});
